/**
 * Admin Dashboard variant of the Salesforce Live Pulse widget.
 *
 * Same layout as the Salesforce Portal widget
 * (SalesforceLivePulseWidget.js) but self-fetches the ``/salesforce/status``
 * endpoint every 30s using the current admin bearer instead of
 * receiving pre-fetched stats from a parent. Auto-refresh + live
 * countdown ticks give Federico / Fabrizio the same "the CRM is
 * alive" feedback Motta gets in her dedicated portal.
 *
 * The visible-role gate is server-side: the ``/salesforce`` router
 * uses ``require_salesforce_reader`` (super_admin | admin | it |
 * salesforce). Viewer role gets a 403 and we render nothing.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { SalesforceLivePulseWidget } from "../salesforce/SalesforceLivePulseWidget";
import { API } from "../../config";

const REFRESH_MS = 30_000;

export const SalesforceLivePulseAdminWidget = () => {
  const [status, setStatus] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [bulkSyncing, setBulkSyncing] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/salesforce/status`);
      setStatus(r.data);
    } catch (e) {
      if (e?.response?.status === 403) setHidden(true);
    }
  };

  useEffect(() => {
    let mounted = true;
    const boot = async () => {
      await load();
    };
    boot();
    const t = setInterval(() => { if (mounted) load(); }, REFRESH_MS);
    return () => {
      mounted = false;
      clearInterval(t);
    };
  }, []);

  // Poll bulk-sync progress every 2s while a job is running so the
  // widget's progress bar reflects reality without waiting for the
  // next 30-second stats refresh.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await axios.get(`${API}/salesforce/customers-sync-summary`);
        const job = r.data?.last_job;
        if (cancelled) return;
        if (job && job.status === "running") {
          setBulkProgress({
            status: "running",
            processed: job.processed || 0,
            total: job.total || 0,
            created: job.created || 0,
            duplicates: job.duplicates || 0,
            failed: job.failed || 0,
          });
        } else {
          setBulkProgress((prev) => {
            if (prev?.status === "running") load();
            return null;
          });
        }
      } catch {
        // silent
      }
    };
    tick();
    const id = setInterval(tick, 2000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  const handleBulkSync = async () => {
    setBulkSyncing(true);
    try {
      const r = await axios.post(`${API}/salesforce/sync-all-customers`, {});
      const total = r.data?.total ?? 0;
      if (total === 0) {
        toast.success("Nessun cliente da sincronizzare");
      } else {
        toast.success(`Sync massiva avviata per ${total} clienti`);
        setBulkProgress({
          status: "running",
          processed: 0,
          total,
          created: 0,
          duplicates: 0,
          failed: 0,
        });
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nell'avvio della sync massiva");
    } finally {
      setBulkSyncing(false);
    }
  };

  if (hidden) return null;
  if (!status) return null;

  return (
    <SalesforceLivePulseWidget
      stats={status.stats || {}}
      configured={status.configured}
      onBulkSync={handleBulkSync}
      bulkSyncing={bulkSyncing}
      bulkProgress={bulkProgress}
    />
  );
};

export default SalesforceLivePulseAdminWidget;
